// Sprint work-segment-collapse (PR-B, D1-D3/D8/D9) — the fold unit between
// two agent messages. `splitWorkSegments` (turn-boundaries.ts) computes
// WHETHER and WHEN a run of render items folds; this component is only the
// chrome + disclosure around that decision — reuses `CollapseHeaderRow`
// (D3 item 13, shared with `AggregationCard`) for the header line and the
// `open && <div>` idiom `AggregationCard` already uses for the panel.
//
// D9 — visual separation: `py-2` on the chrome root adds 8px above and
// below (24px total with the section's own `space-y-4`), the same
// `gap-block` role `CompactionDivider.tsx` already uses for "between
// independent blocks" — no new token.

import type { ReactNode } from 'react';
import { formatWorkedFor, formatClockTime } from '../../lib/tool-rendering/format-elapsed';
import { useTranslation } from '../../i18n';
import { CollapseHeaderRow } from './CollapseHeaderRow';
import { Loader } from '../ai-elements/loader';
import type { WorkSegment as WorkSegmentData } from '../../lib/tool-rendering/turn-boundaries';

export interface WorkSegmentProps {
  segment: WorkSegmentData;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

export function WorkSegment({ segment, open, onToggle, children }: WorkSegmentProps) {
  const { t } = useTranslation();
  const worked = formatWorkedFor(segment.durationMs, t);
  const time = formatClockTime(segment.startedAtMs);
  // D6 — tres formas de cabecera. `live`: desde cuándo se trabaja (sin `time` utilizable cae en
  // `progress.working`, clave existente — nunca "Worked for" y nunca un NaN). `unconfirmed`
  // (C9): la MISMA plantilla `workSegment.header`, con la etiqueta de "sin confirmación de fin"
  // en el hueco de `{worked}` — cero claves nuevas. Ninguno de los dos: exactamente lo de hoy.
  const headline = segment.live
    ? (time ? t('workSegment.headerLive', { time }) : t('progress.working'))
    : segment.unconfirmed
      ? (time ? t('workSegment.header', { worked: t('subagent.unconfirmed'), time }) : t('subagent.unconfirmed'))
      : (time ? t('workSegment.header', { worked, time }) : worked);
  const failureLabel =
    segment.failureCount === 0
      ? null
      : segment.failureCount === 1
        ? t('actionRow.failedOne')
        : t('actionRow.failedMany', { count: segment.failureCount });

  return (
    <CollapseHeaderRow
      icon="Wrench"
      iconClassName="text-muted-foreground"
      headline={headline}
      headlineClassName="text-muted-foreground"
      failureLabel={failureLabel}
      failureTestId="work-segment-failures"
      meta={null}
      trailing={
        segment.live ? (
          <span
            className="inline-flex shrink-0 items-center"
            role="status"
            aria-label={t('subagent.running')}
            data-testid="work-segment-live"
          >
            <Loader size={12} />
          </span>
        ) : undefined
      }
      open={open}
      onToggle={onToggle}
      rootClassName="py-2"
      rootTestId="work-segment"
      headerTestId="work-segment-header"
      rootProps={{
        'data-segment-id': segment.key,
        'data-folded': String(!open),
        'data-failures': String(segment.failureCount),
        'data-live': String(segment.live),
        'data-unconfirmed': String(segment.unconfirmed),
      }}
      ariaLabel={t('workSegment.ariaLabel')}
    >
      {open && (
        <div className="space-y-4 pb-1" data-testid="work-segment-panel">
          {children}
        </div>
      )}
    </CollapseHeaderRow>
  );
}
